import type { TaskItem } from '../../tasks/api/tasks.client.js';
import { Link } from 'react-router-dom';
import { useTranslation } from '@govard/i18n-core/react';
import { Progress } from 'antd';
import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { TagChip } from '../../../components/entity-detail/TagChip.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { winProbabilityFactorText } from '../../../shared/format/win-probability-labels.js';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelative,
  formatRelativeShort,
} from '../../../shared/format/entity-detail.js';
import { formatPersonName } from '../../../shared/format/index.js';
import { scoreToneColor } from '../lib/score-tone.js';
import { DealCompanyField } from './DealCompanyField.js';
import type {
  DealDetail,
  DealSalesHealthSummary,
  DealWinProbabilitySummary,
} from '../api/deals.client.js';
import type { DealHistoryItem } from '../hooks/useDealHistory.js';

export function DealSidebar({
  deal,
  health,
  history,
  tasks,
  winProbability,
}: {
  deal: DealDetail;
  health: DealSalesHealthSummary | undefined;
  history: DealHistoryItem[];
  tasks: TaskItem[];
  winProbability: DealWinProbabilitySummary | undefined;
}) {
  const { t, tc } = useTranslation();
  const last = history.find((item) => item.kind !== 'deadline_passed');
  const date = last?.date ?? deal.lastActivityAt ?? deal.createdAt;
  const event = last?.kind.includes('stage')
    ? 'stage'
    : last?.kind.includes('task')
      ? 'task'
      : last?.kind.includes('note')
        ? 'note'
        : last?.kind.includes('meeting')
          ? 'meeting'
          : last?.kind.includes('file') || last?.kind.includes('attachment')
            ? 'file'
            : 'other';
  const reasons = health?.reasons
    ?.map((reason) => t(`crm.deal_detail.reason_${reason}`))
    .join(' · ');
  const healthScore =
    health && typeof health.healthScore === 'number' && Number.isFinite(health.healthScore)
      ? `${Math.round(health.healthScore)}%`
      : '—';
  const risk = health ? t(`crm.deal_detail.risk_${health.healthLevel}`) : undefined;
  const openTasks = tasks.filter((task) => task.status !== 'done' && task.status !== 'cancelled');
  const taskCounts = `${openTasks.length} / ${openTasks.filter((task) => task.dueAt && Date.parse(task.dueAt) < Date.now()).length}`;
  return (
    <>
      <section className="entity-panel">
        <h2>{t('crm.deal_detail.summary')}</h2>
        <div className="deal-summary-grid">
          <EntityMetric
            label={t('crm.deal_card.form.amount')}
            value={
              <span style={MONO_NUMERIC_STYLE}>{formatMoney(deal.amount, deal.currency)}</span>
            }
            exact={formatMoney(deal.amount, deal.currency)}
          />
          <EntityMetric
            testId="deal-health-score"
            label={t('crm.deal_detail.health_short')}
            value={healthScore}
            exact={[healthScore, risk, reasons].filter(Boolean).join(' · ')}
            hint={risk}
          />
          <EntityMetric
            testId="deal-activity"
            label={t('crm.deal_detail.activity_short')}
            value={date ? formatRelativeShort(date) : t('crm.entity_detail.empty_history')}
            exact={date ? `${formatRelative(date)} · ${formatDateTime(date)}` : undefined}
            hint={
              date
                ? `${last ? t(`crm.deal_detail.event_${event}`) : t('crm.entity_detail.created')} · ${formatDate(date)}`
                : undefined
            }
          />
          <EntityMetric
            label={t('crm.deal_detail.tasks_short')}
            labelTooltip={t('crm.deal_detail.task_counts_hint')}
            value={taskCounts}
            exact={taskCounts}
          />
        </div>
      </section>
      {winProbability ? (
        <section className="entity-panel" data-testid="deal-win-probability">
          <h2>{t('crm.win_probability.title')}</h2>
          <div className="deal-win-probability-score">
            <span
              className="deal-win-probability-value"
              data-testid="deal-win-probability-value"
              style={MONO_NUMERIC_STYLE}
            >
              {winProbability.score}%
            </span>
            <Progress
              percent={winProbability.score}
              size="small"
              showInfo={false}
              strokeColor={scoreToneColor(winProbability.score)}
              railColor="var(--ant-color-fill-quaternary)"
              className="deal-win-probability-progress"
            />
          </div>
          <ul className="deal-win-probability-factors">
            {winProbability.factors.map((factor) => (
              <li key={`${factor.code}-${factor.impact}`} className="deal-win-probability-factor">
                <span className="deal-win-probability-factor-text">
                  {winProbabilityFactorText({ t, tc }, factor)}
                </span>
                {factor.impact !== 0 ? (
                  <span
                    className={
                      factor.impact > 0
                        ? 'deal-win-probability-impact deal-win-probability-impact-positive'
                        : 'deal-win-probability-impact deal-win-probability-impact-negative'
                    }
                    style={MONO_NUMERIC_STYLE}
                  >
                    {factor.impact > 0 ? `+${factor.impact}` : factor.impact}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="entity-panel">
        <h2>{t('crm.deal_detail.details')}</h2>
        <EntityFieldList
          fields={[
            ...(health &&
            typeof health.closeProbability === 'number' &&
            Number.isFinite(health.closeProbability) &&
            health.closeProbability > 0
              ? [
                  {
                    key: 'closeProbability',
                    label: t('crm.deal_detail.probability'),
                    value: `${health.closeProbability}%`,
                  },
                ]
              : []),
            {
              key: 'source',
              label: t('crm.deal_card.form.source'),
              value: deal.source ? getEnumLabel('dealSource', deal.source, t) : null,
            },
            ...(deal.owner
              ? [
                  {
                    key: 'owner',
                    label: t('crm.deal_card.summary.owner'),
                    value: deal.owner.name || deal.owner.email,
                  },
                ]
              : []),
            {
              key: 'contact',
              label: t('crm.deal_card.details.contact'),
              value: deal.contact ? (
                <Link to={`/contacts/${deal.contact.id}`}>
                  {formatPersonName(deal.contact.firstName, deal.contact.lastName)}
                </Link>
              ) : null,
            },
            {
              key: 'company',
              label: t('crm.deal_detail.company'),
              value: <DealCompanyField deal={deal} />,
            },
            {
              key: 'tags',
              label: t('crm.entity_detail.tags'),
              value: deal.tags.length ? (
                <div className="deal-tags">
                  {deal.tags.map((tag) => (
                    <TagChip key={tag} value={tag} />
                  ))}
                </div>
              ) : null,
            },
            {
              key: 'created',
              label: t('crm.entity_detail.created'),
              value: formatDateTime(deal.createdAt),
            },
            ...(deal.updatedAt
              ? [
                  {
                    key: 'updated',
                    label: t('crm.entity_detail.updated'),
                    value: formatDateTime(deal.updatedAt),
                  },
                ]
              : []),
          ]}
        />
      </section>
    </>
  );
}
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
